import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import mammoth from 'mammoth';
import { useAppSelector, useAppDispatch } from '../../../../store/hooks';
import { clearError, updateBookData } from '../../../../store/slices/bookSlice';
import { useBookAPI } from '../../../../hooks/useBookAPI';
import { endpoints, baseUrl } from '../../../../api/api';
import SpellcheckSection from './components/SpellcheckSection';
import UploadedFileCard from './components/UploadedFileCard';
import UploadSection from './components/UploadSection';
import DocxImportModal from './components/DocxImportModal';
import { parseDocxSections } from '../../../../utils/parseDocxSections';
import { uploadImagesInHtml } from '../../../NotionEditor/hooks/useImageUpload';
import { Section } from '../../../NotionEditor/types';
import { trackJourneySelected, trackUploadStart, trackUploadComplete, trackUploadFailed, trackDocxImportInitiated, trackDocxImportParseFailed, trackDocxImportPreviewShown, trackDocxImportCompleted, trackDocxImportFailed, trackDocxReimportInitiated, trackFileDownloadFailed, trackPdfResizeInitiated, trackPdfResizeCompleted, trackPdfResizeFailed } from '../../../../utils/analytics';
import { useAlert } from '../../../../components/AlertBanner';

const MAX_FILE_SIZE = 100 * 1024 * 1024;

// Mirrors the backend's PDF_OCR_IMPORT_ENABLED kill switch (pdfImportController.js)
// — the OCR-convert path's pdftoppm rendering is CPU-bound and currently
// competes with Puppeteer for limited container cores, so it's off until the
// container is sized up. When off, skip the choice modal entirely and go
// straight to the resize path — there's no real "choice" to offer with only
// one option available.
const PDF_OCR_IMPORT_ENABLED = process.env.REACT_APP_PDF_OCR_IMPORT_ENABLED === 'true';

const isPdfFile = (filename: string | null): boolean => {
  if (!filename) return false;
  return filename.toLowerCase().endsWith('.pdf');
};

// Used to gate the Meta Pixel Lead event to the genuinely first time a book
// gets real content — a Replace upload or a DOCX re-import shouldn't refire it.
const hasRealSectionContent = (sections: any[] | undefined): boolean =>
  !!sections?.some((s) => s?.content && s.content.trim() !== '' && s.content !== '<p></p>');

const BookFormattingStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const isStep3Complete = useAppSelector((state) => state.book.isStep3Complete);
  const bookData = useAppSelector((state) => state.book.bookData);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [showDesktopOnly, setShowDesktopOnly] = useState(false);
  const [isParsingDocx, setIsParsingDocx] = useState(false);
  const [docxSections, setDocxSections] = useState<Section[] | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');
  const [importStep, setImportStep] = useState<1 | 2 | 3>(1);
  const importStartTimeRef = useRef<number>(0);
  const [isPdfImporting, setIsPdfImporting] = useState(false);
  const [pdfImportProgress, setPdfImportProgress] = useState<{ currentPage: number; totalPages: number } | null>(null);
  const [pdfChoiceBookId, setPdfChoiceBookId] = useState<string | null>(null);
  const [isResizingPdf, setIsResizingPdf] = useState(false);

  const { saveBook, saveAndContinue, isSaving, error, bookId, startPdfImport, getPdfImportStatus, resizePdf } = useBookAPI();

  useEffect(() => {
    if (bookData.uploadedFile) {
      setUploadedFileName(bookData.uploadedFile);
    }
  }, [bookData.uploadedFile]);

  const hasContent = !!bookData.content?.html && bookData.content.html.trim().length > 0;
  const hasUploadedFile = !!bookData.uploadedFile || !!uploadedFileName;

  const validateFile = (file: File, allowedTypes: string[]): string | null => {
    if (!allowedTypes.includes(file.type)) return 'Invalid file type. Please upload a PDF file.';
    if (file.size > MAX_FILE_SIZE) return 'File size exceeds 100MB limit.';
    return null;
  };

  // Polls until the backend's OCR-based conversion finishes, then hands the
  // resulting Section[] to the same DocxImportModal/handleDocxImportConfirm
  // path DOCX import already uses — from here on, a PDF-origin book and a
  // typed one are indistinguishable. Kicked off only when the author picks
  // "Convert to editable content" in the post-upload choice modal.
  const pollPdfImportStatus = async (currentBookId: string) => {
    setIsPdfImporting(true);
    setPdfImportProgress(null);
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2500));
        const status = await getPdfImportStatus(currentBookId);
        if (!status) continue;

        if (typeof status.totalPages === 'number') {
          setPdfImportProgress({ currentPage: status.currentPage || 0, totalPages: status.totalPages });
        }

        if (status.status === 'complete') {
          const sections: Section[] = (status.sections || []).map((s: any) => ({
            id: s.id,
            title: s.title,
            content: s.content,
            type: s.type,
          }));
          setDocxSections(sections);
          break;
        }

        if (status.status === 'failed') {
          showAlert(status.error || 'Could not convert this PDF automatically. Please try re-uploading.', 'error');
          break;
        }
      }
    } finally {
      setIsPdfImporting(false);
      setPdfImportProgress(null);
    }
  };

  const uploadFile = async (file: File) => {
    setUploadError(null);
    setIsUploading(true);
    const fileExtension = file.name.split('.').pop() || 'unknown';
    trackUploadStart(fileExtension, 'upload');
    // Captured before any dispatch below overwrites it — reflects whether this
    // book had a manuscript already, not the post-upload state.
    const hadNoContentBefore = !bookData.uploadedFile && !hasRealSectionContent(bookData.content?.json?.sections);
    try {
      let currentBookId = bookId;
      if (!currentBookId) {
        const saveResult = await saveBook();
        if (saveResult.meta.requestStatus === 'fulfilled') {
          currentBookId = saveResult.payload?.book?._id || saveResult.payload?.book?.id;
        }
        if (!currentBookId) throw new Error('Failed to save book. Please try again.');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('fileType', 'manuscript');
      formData.append('bookId', currentBookId);

      const response = await axios.post(endpoints.uploadFile, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setUploadedFileName(file.name);
        // The backend already computed the real page count straight off the
        // uploaded PDF (via pdf-lib) — independent of, and available before,
        // either the convert/resize choice. No manual entry needed.
        dispatch(updateBookData({
          uploadedFile: response.data.file.filename,
          ...(typeof response.data.pageCount === 'number' ? { pageCount: response.data.pageCount } : {}),
        }));
        // Clear any stale editor content from the DB — the uploaded PDF is now the
        // source of truth. Silently ignored if the book had no editor content.
        await axios.post(endpoints.switchToPdf.replace(':id', currentBookId)).catch(() => {});
        trackUploadComplete(fileExtension, Math.round((file.size / (1024 * 1024)) * 100) / 100, 'upload', hadNoContentBefore);

        if (PDF_OCR_IMPORT_ENABLED) {
          // Let the author decide what happens to their PDF next — convert it
          // into editable content, or just resize it to fit while keeping
          // their original design untouched.
          setPdfChoiceBookId(currentBookId);
        } else {
          // OCR-convert is disabled — resize is the only option, so there's
          // no real choice to present. Run it automatically.
          runPdfResize(currentBookId);
        }
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err: any) {
      trackUploadFailed(err.response?.data?.message || err.message || 'Failed to upload file');
      setUploadError(err.response?.data?.message || err.message || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  // "Convert to editable content" — the OCR pipeline: re-typesets everything
  // through IndiePress's own editor/PDF pipeline, fixing page count but
  // discarding the original PDF's fonts/layout.
  const handleChoosePdfConvert = async () => {
    const currentBookId = pdfChoiceBookId;
    setPdfChoiceBookId(null);
    if (!currentBookId) return;
    const importResult = await startPdfImport(currentBookId);
    if (importResult?.success) {
      pollPdfImportStatus(currentBookId);
    } else {
      showAlert(importResult?.message || 'Could not start PDF conversion. Please try re-uploading.', 'error');
    }
  };

  // "Keep my original design" — pure geometric page resize (pdf-lib
  // embedPages/drawPage on the backend): preserves every font/layout choice
  // exactly, just fits pages to the chosen trim size. Doesn't fix page count.
  // Shared by the choice-modal button and the OCR-disabled auto-run path below.
  const runPdfResize = async (currentBookId: string) => {
    setIsResizingPdf(true);
    trackPdfResizeInitiated(bookData.bookSize || 'unknown');
    try {
      const result = await resizePdf(currentBookId);
      if (result?.success) {
        // Resizing can't change page count — pdf-lib already knows it exactly
        // from the source PDF, so there's no need to make the author type it in.
        if (typeof result.pageCount === 'number') {
          dispatch(updateBookData({ pageCount: result.pageCount }));
        }
        // The backend already swapped book.uploadedFile to the resized file —
        // update our local copy of the filename too, otherwise the download
        // button keeps pointing at the pre-resize file by its old filename.
        if (result.filename) {
          setUploadedFileName(result.filename);
          dispatch(updateBookData({ uploadedFile: result.filename }));
        }
        trackPdfResizeCompleted({ book_size: bookData.bookSize || 'unknown', page_count: result.pageCount });
        showAlert(
          result.alreadyCorrectSize
            ? 'Your PDF already matches your chosen book size — no changes needed.'
            : 'Your PDF has been resized to fit your chosen book size — original design preserved.',
          'success'
        );
      } else {
        trackPdfResizeFailed(result?.message || 'unknown');
        showAlert(result?.message || 'Could not resize this PDF. Please try again.', 'error');
      }
    } finally {
      setIsResizingPdf(false);
    }
  };

  const handleChoosePdfResize = async () => {
    const currentBookId = pdfChoiceBookId;
    setPdfChoiceBookId(null);
    if (!currentBookId) return;
    await runPdfResize(currentBookId);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, allowedTypes: string[]) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validationError = validateFile(file, allowedTypes);
    if (validationError) { setUploadError(validationError); return; }
    await uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, allowedTypes: string[]) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const validationError = validateFile(file, allowedTypes);
    if (validationError) { setUploadError(validationError); return; }
    await uploadFile(file);
  };

  const handleRemoveFile = async () => {
    const hasCover = !!bookData.coverImage;
    const message = hasCover
      ? 'Removing this file will also delete your uploaded cover image. This cannot be undone. Continue?'
      : 'Remove the uploaded manuscript? This cannot be undone.';
    if (!window.confirm(message)) return;
    setUploadedFileName(null);
    dispatch(updateBookData({ uploadedFile: null, ...(hasCover ? { coverImage: null } : {}) }));
    // Persist the removal server-side — otherwise the book doc still has the
    // old file, and re-uploading afterward can resize from that stale file's
    // bytes instead of the fresh upload (see removeUploadedFile on the backend).
    if (bookId) {
      try {
        await axios.delete(endpoints.removeUploadedFile.replace(':id', bookId));
      } catch (err: any) {
        console.error('Failed to remove uploaded file from server:', err);
      }
    }
  };

  const handleDownloadFile = async () => {
    const filename = uploadedFileName || bookData.uploadedFile;
    if (!filename) return;
    try {
      const downloadUrl = `${baseUrl}${endpoints.downloadFileByFilename.replace(':filename', filename)}`;
      const response = await axios.get(downloadUrl, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      trackFileDownloadFailed(err?.response?.data?.message || err?.message || 'unknown');
      setUploadError('Failed to download file. Please try again.');
    }
  };

  const handleStartEditing = async () => {
    trackJourneySelected('inline_editor');
    if (window.innerWidth < 768) {
      setShowDesktopOnly(true);
      return;
    }
    if (!bookId) {
      const result = await saveBook();
      if (result.meta.requestStatus === 'fulfilled') {
        const newBookId = result.payload?.book?._id || result.payload?.book?.id;
        if (newBookId) { navigate(`/notion?bookId=${newBookId}`); return; }
      }
      showAlert('Please save your book first before editing content.', 'warning');
      return;
    }
    navigate(`/notion?bookId=${bookId}`);
  };

  const stripDocxArtifacts = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Remove footnote definition list
    Array.from(doc.querySelectorAll('ol')).forEach(ol => {
      if (ol.querySelector('li[id^="footnote-"]')) ol.remove();
    });
    // Remove footnote inline references and their <sup> wrappers
    doc.querySelectorAll('a[href^="#footnote-"]').forEach(a => {
      let el: Element = a;
      while (el.parentElement?.tagName === 'SUP') el = el.parentElement;
      el.remove();
    });
    // Internal anchor links (href="#...") are TOC/navigation artifacts — remove entirely.
    // External links — keep the visible text, strip only the anchor wrapper.
    doc.querySelectorAll('a[href]').forEach(a => {
      const href = a.getAttribute('href') ?? '';
      if (href.startsWith('#')) {
        a.remove();
      } else {
        a.replaceWith(...Array.from(a.childNodes));
      }
    });
    return doc.body.innerHTML;
  };

  const handleDocxSelect = async (file: File) => {
    if (window.innerWidth < 768) { setShowDesktopOnly(true); return; }
    setIsParsingDocx(true);
    setUploadError(null);
    trackDocxImportInitiated(Math.round(file.size / 1024));
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          // Strip native Word TOC paragraph styles before they become visible content.
          // These produce "Chapter 1..........5" lines that are meaningless in IndiePress
          // since the platform generates its own TOC from section titles.
          styleMap: [
            "p[style-name='TOC 1'] => ",
            "p[style-name='TOC 2'] => ",
            "p[style-name='TOC 3'] => ",
            "p[style-name='TOC 4'] => ",
            "p[style-name='TOC Heading'] => ",
          ],
          convertImage: mammoth.images.imgElement(async (image) => {
            const base64 = await image.read('base64');
            return { src: `data:${image.contentType};base64,${base64}` };
          }),
        },
      );
      const { sections, tocRemoved } = parseDocxSections(stripDocxArtifacts(result.value));
      const ordered = [
        ...sections.filter(s => s.type === 'front-matter'),
        ...sections.filter(s => s.type === 'chapter'),
        ...sections.filter(s => s.type === 'end-matter'),
      ];
      const imagesCount = ordered.reduce((sum, s) => sum + (s.content.match(/src="data:image/g)?.length ?? 0), 0);
      trackDocxImportPreviewShown({
        sections_count: ordered.length,
        chapters: ordered.filter(s => s.type === 'chapter').length,
        front_matter: ordered.filter(s => s.type === 'front-matter').length,
        end_matter: ordered.filter(s => s.type === 'end-matter').length,
        toc_removed: tocRemoved,
        images_count: imagesCount,
      });
      setDocxSections(ordered);
      if (tocRemoved > 0) {
        showAlert(
          `Table of Contents removed — IndiePress generates this automatically from your chapter titles.`,
          'success',
        );
      }
    } catch (err: any) {
      trackDocxImportParseFailed(err?.message ?? 'parse_error');
      setUploadError('Could not read the document. Please make sure it is a valid .docx file.');
    } finally {
      setIsParsingDocx(false);
    }
  };

  const handleDocxImportConfirm = async (sections: Section[]) => {
    setIsImporting(true);
    setDocxSections(null);
    importStartTimeRef.current = Date.now();
    // Captured before the dispatch below overwrites it — reflects whether this
    // book had real content before this specific import (a re-import replaces
    // existing content, it isn't a fresh Lead).
    const hadNoContentBefore = !bookData.uploadedFile && !hasRealSectionContent(bookData.content?.json?.sections);
    try {
      // Ensure book exists before uploading images (images need a bookId)
      let currentBookId = bookId;
      if (!currentBookId) {
        setImportStep(1);
        setImportStatus('Creating book…');
        const saveResult = await saveBook();
        if (saveResult.meta.requestStatus === 'fulfilled') {
          currentBookId = saveResult.payload?.book?._id || saveResult.payload?.book?.id;
        }
        if (!currentBookId) throw new Error('Failed to save book. Please try again.');
      }

      // Count total images across all sections for progress display
      const totalImages = sections.reduce((sum, s) => {
        return sum + (s.content.match(/src="data:image\/[^;]+;base64,/g)?.length ?? 0);
      }, 0);
      let uploadedSoFar = 0;

      // Upload base64 images section by section; replace with CDN URLs before saving
      setImportStep(2);
      setImportStatus(totalImages > 0 ? `Uploading images… (0 of ${totalImages})` : 'Saving…');
      const processedSections: Section[] = [];
      for (const section of sections) {
        const processedContent = await uploadImagesInHtml(
          section.content,
          currentBookId,
          (_sectionUploaded, _sectionTotal) => {
            uploadedSoFar++;
            setImportStatus(`Uploading images… (${uploadedSoFar} of ${totalImages})`);
          },
        );
        processedSections.push({ ...section, content: processedContent });
      }

      setImportStep(3);
      setImportStatus('Saving…');
      const combinedHtml = processedSections.map(s =>
        `<div class="section" data-section-id="${s.id}" data-section-type="${s.type}"><h2>${s.title}</h2>${s.content}</div>`
      ).join('');
      const payload = { content: { html: combinedHtml, json: { sections: processedSections }, lastSavedAt: new Date().toISOString() } };

      await axios.put(endpoints.updateBook.replace(':id', currentBookId), payload);
      dispatch(updateBookData({ content: { html: combinedHtml, json: { sections: processedSections } } }));
      trackDocxImportCompleted({
        duration_ms: Date.now() - importStartTimeRef.current,
        images_uploaded: uploadedSoFar,
        book_language: bookData.language ?? 'unknown',
        isFirstRealContent: hadNoContentBefore,
      });
      navigate(`/notion?bookId=${currentBookId}`);
    } catch (err: any) {
      const errMsg = err.response?.data?.message || err.message || 'unknown';
      trackDocxImportFailed(errMsg);
      showAlert(errMsg === 'unknown' ? 'Import failed. Please try again.' : errMsg, 'error');
    } finally {
      setIsImporting(false);
      setImportStatus('');
      setImportStep(1);
    }
  };

  const handleDeleteImport = () => {
    if (!window.confirm('Start over with a new file? Your current import will be replaced once you complete the new import.')) return;
    trackDocxReimportInitiated();
    // Clear only the local Redux state — the backend content is preserved until
    // the user completes a new import, which overwrites it. This avoids fighting
    // the backend guard that blocks blank-wipe saves.
    dispatch(updateBookData({ content: { html: '', json: { sections: [] } } }));
  };

  const handleSaveToDrafts = async () => {
    const result = await saveBook();
    if (result.meta.requestStatus === 'fulfilled') showAlert('Saved to drafts successfully!', 'success');
  };

  const handleSaveAndContinue = async () => {
    if (!isStep3Complete) {
      showAlert('Please upload a file before continuing.', 'warning');
      return;
    }
    // All editor-content books go through the Preview screen — it's where Preview
    // now lives (removed from the editor toolbar to bound how often it can be
    // triggered). Books that don't require approval (pre-existing, uploaded-file)
    // can still preview there, they just aren't blocked from continuing without it —
    // see BookPreviewStep's Continue button.
    await saveAndContinue(hasContent ? 'book-preview' : 'book-cover');
  };

  return (
    <div className="bg-white md:bg-transparent min-h-screen md:min-h-0">
      <div className="hidden md:block mb-6">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
          <span>←</span><span>Go Back to Dashboard</span>
        </button>
      </div>

      <div className="md:hidden bg-gray-50 px-4 py-3 border-b border-gray-200">
        <p className="text-xs text-gray-600">Book Info › Book Form Factor › Book Formatting</p>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 mx-4 md:mx-0">
          <div className="flex justify-between items-start">
            <p className="text-red-700 text-sm">{error}</p>
            <button onClick={() => dispatch(clearError())} className="text-red-600 hover:text-red-800 text-sm underline ml-4">Dismiss</button>
          </div>
        </div>
      )}

      {showDesktopOnly && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mx-4 md:mx-0 mb-4 flex items-start gap-3">
          <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <p className="text-amber-900 text-sm font-medium">Desktop Only</p>
            <p className="text-amber-700 text-xs mt-1">
              The editor is accessible from Desktop only. Please open this page on a Desktop browser to use the editor.
            </p>
          </div>
          <button onClick={() => setShowDesktopOnly(false)} className="text-amber-500 hover:text-amber-700 text-lg leading-none">&times;</button>
        </div>
      )}

      <div className="bg-white md:bg-white p-4 md:p-8 rounded-none md:rounded-lg md:border md:border-gray-200 md:shadow-sm">
        {hasContent || hasUploadedFile ? (
          <div className="space-y-6">
            {hasContent && (
              <div className="flex flex-col items-center">
                <div className="bg-orange-50 rounded-lg p-8 w-48 h-64 flex flex-col items-center justify-start text-center mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{bookData.title || 'Book Title'}</h3>
                  <p className="text-sm text-gray-600 mb-4">{bookData.subtitle || 'Subtitle'}</p>
                  <p className="text-sm text-gray-700">{bookData.author || 'Author Name'}</p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={handleStartEditing} className="bg-gray-900 text-white px-6 py-2.5 rounded-md hover:bg-gray-800 font-medium text-sm">
                    Continue Editing
                  </button>
                  <button
                    onClick={handleDeleteImport}
                    className="flex items-center gap-1.5 text-sm text-red-500 hover:text-red-700 font-medium transition-colors"
                    title="Delete imported content and re-import"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete & Re-import
                  </button>
                </div>
              </div>
            )}

            {hasUploadedFile && !hasContent && (
              <UploadedFileCard
                fileName={uploadedFileName || 'Uploaded File'}
                isPdf={isPdfFile(uploadedFileName || bookData.uploadedFile)}
                isUploading={isUploading}
                onDownload={handleDownloadFile}
                onRemove={handleRemoveFile}
              />
            )}

            <SpellcheckSection />

            <div className="flex gap-3 pt-6 justify-start">
              <button onClick={handleSaveToDrafts} disabled={isSaving} className="bg-transparent text-orange-500 px-8 py-3 rounded-lg border-2 border-orange-500 hover:bg-orange-50 font-medium disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleSaveAndContinue} disabled={isSaving} className="bg-gray-900 text-white px-8 py-3 rounded-lg hover:bg-gray-800 font-medium disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <UploadSection
              isUploading={isUploading}
              isParsingDocx={isParsingDocx}
              uploadError={uploadError}
              onFileChange={handleFileChange}
              onDrop={handleDrop}
              onStartEditing={handleStartEditing}
              onClearError={() => setUploadError(null)}
              onDocxSelect={handleDocxSelect}
            />
            <div className="md:hidden flex flex-col items-center space-y-3 pt-4">
              <button onClick={handleSaveToDrafts} disabled={isSaving} className="bg-white text-orange-500 px-8 py-3 rounded-md border-2 border-orange-500 font-medium disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleSaveAndContinue} disabled={isSaving} className="bg-gray-900 text-white px-8 py-3 rounded-md font-medium disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Continue'}
              </button>
            </div>
            <div className="hidden md:flex gap-3 pt-6 justify-start">
              <button onClick={handleSaveToDrafts} disabled={isSaving} className="bg-transparent text-orange-500 px-8 py-3 rounded-lg border-2 border-orange-500 font-medium disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleSaveAndContinue} disabled={isSaving} className="bg-gray-900 text-white px-8 py-3 rounded-lg font-medium disabled:opacity-50">
                {isSaving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </>
        )}

      </div>

      {docxSections && !isImporting && (
        <DocxImportModal
          sections={docxSections}
          isImporting={false}
          importStatus={importStatus}
          onConfirm={handleDocxImportConfirm}
          onCancel={() => setDocxSections(null)}
        />
      )}

      {/* Post-upload choice — convert to editable content (OCR, re-typesets,
          fixes page count) vs keep the original design (resize only) */}
      {pdfChoiceBookId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">How should we handle your PDF?</h3>
            <p className="text-sm text-gray-500 mb-6">Pick one — you can re-upload later to change your mind.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={handleChoosePdfConvert}
                className="text-left border-2 border-gray-200 hover:border-gray-900 rounded-lg p-4 transition-colors"
              >
                <p className="text-sm font-semibold text-gray-900 mb-1">Convert to editable content</p>
                <p className="text-xs text-gray-500">
                  We'll read your PDF and rebuild it in our editor with accurate page count for your chosen book size.
                  Recommended for most authors.
                </p>
              </button>
              <button
                onClick={handleChoosePdfResize}
                className="text-left border-2 border-gray-200 hover:border-gray-900 rounded-lg p-4 transition-colors"
              >
                <p className="text-sm font-semibold text-gray-900 mb-1">Keep my original design</p>
                <p className="text-xs text-gray-500">
                  We'll just resize your pages to fit your chosen book size — fonts, layout and images stay exactly
                  as you designed them. Page count won't change.
                </p>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resizing-in-place overlay — for the "keep my original design" choice */}
      {isResizingPdf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-sm flex flex-col gap-4 text-center">
            <div className="w-8 h-8 mx-auto border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">Resizing your PDF</h3>
              <p className="text-xs text-gray-500 mt-1">Just a moment — your original design is being preserved.</p>
            </div>
          </div>
        </div>
      )}

      {/* PDF-to-editor conversion overlay — runs after the author picks
          "Convert to editable content" above */}
      {isPdfImporting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-sm flex flex-col gap-4 text-center">
            <div className="w-8 h-8 mx-auto border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
            <div>
              <h3 className="text-base font-semibold text-gray-900">Converting your PDF</h3>
              <p className="text-xs text-gray-500 mt-1">
                {pdfImportProgress && pdfImportProgress.totalPages > 0
                  ? `Reading page ${pdfImportProgress.currentPage} of ${pdfImportProgress.totalPages}…`
                  : 'This can take a few minutes for longer books — please don\'t close this window.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Full-screen import progress overlay — replaces modal during import */}
      {isImporting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-sm flex flex-col gap-6">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Importing your manuscript</h3>
              <p className="text-xs text-gray-400 mt-1">Please don't close this window</p>
            </div>
            <div className="space-y-4">
              {[
                { label: 'Preparing book', detail: '' },
                { label: 'Processing images', detail: importStep === 2 ? importStatus.replace('Uploading images… ', '') : '' },
                { label: 'Saving to book', detail: '' },
              ].map((step, i) => {
                const stepNum = i + 1;
                const done = importStep > stepNum;
                const active = importStep === stepNum;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                      done ? 'bg-green-500' : active ? 'bg-orange-500' : 'bg-gray-200'
                    }`}>
                      {done ? (
                        <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : active ? (
                        <div className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${active ? 'text-gray-900' : done ? 'text-gray-500' : 'text-gray-400'}`}>
                        {step.label}
                      </p>
                      {active && step.detail && (
                        <p className="text-xs text-gray-400 truncate">{step.detail}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookFormattingStep;
